import * as React from 'react'
import { Button, Input, Icon, Select, message, Dropdown, Menu, Row } from 'antd'
import { Filter } from '../../FilterStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { DateFilter } from '../../../DashboardReports/Financial/ReportFilter/DateFilter'
import { IBaseProduct, InvoiceStatus, InvoicePaidStatus } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { AsyncProductInput } from '../../../../../components/GrInput/AsyncProductInput'
import { inject, observer } from 'mobx-react'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { browserHistory } from 'react-router'

interface Props {
  invoiceFilter: Filter
  loadInvoices: (offset?: number) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isFilterApplying: boolean
  selectedAddOnFilterNames: AddOnInvoiceListFilterType[]
}

enum AddOnInvoiceListFilterType {
  PAID_STATUS = 'Paid status',
  EMPLOYEE = 'Employee',
  VENDOR = 'Vendor',
  PRODUCTS = 'Products',
  CREATED_TIME = 'Created time',
}

interface AddOnFilterList {
  [key: string]: {
    queryKeys: string[]
    renderItem: JSX.Element
  }
}

@inject('currentCompanyStore')
@observer
export class InvoiceListFilter extends React.Component<Props & InjectedProps, State> {
  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      isFilterApplying: false,
      selectedAddOnFilterNames: [],
    }
  }

  componentDidMount = () => {
    const { query } = browserHistory.getCurrentLocation()
    const queryKeys = Object.keys(query)

    const addOnFiltersToShow: AddOnInvoiceListFilterType[] = []

    if (queryKeys.includes('paid')) {
      addOnFiltersToShow.push(AddOnInvoiceListFilterType.PAID_STATUS)
    }

    // Add add-on filters based on query params
    if (queryKeys.includes('employeeId')) {
      addOnFiltersToShow.push(AddOnInvoiceListFilterType.EMPLOYEE)
    }

    if (queryKeys.includes('supplierId')) {
      addOnFiltersToShow.push(AddOnInvoiceListFilterType.VENDOR)
    }

    if (queryKeys.includes('selectedProducts')) {
      addOnFiltersToShow.push(AddOnInvoiceListFilterType.PRODUCTS)
    }

    if (queryKeys.includes('startDate') || queryKeys.includes('endDate')) {
      addOnFiltersToShow.push(AddOnInvoiceListFilterType.CREATED_TIME)
    }

    this.setSelectedAddOnFilterNames(addOnFiltersToShow)
  }

  setIsFilterApplying = (isFilterApplying: boolean) => this.setState({ isFilterApplying })

  setSelectedAddOnFilterNames = (selectedAddOnFilterNames: AddOnInvoiceListFilterType[]) =>
    this.setState({ selectedAddOnFilterNames })

  setWithoutExistingFilter = (selectedFilterName: AddOnInvoiceListFilterType) => {
    const newFilters = this.state.selectedAddOnFilterNames.filter(
      (filter) => filter !== selectedFilterName,
    )

    this.setSelectedAddOnFilterNames(newFilters)

    this.props.invoiceFilter.apply()
  }

  toggleAppliedFilters = (selectedFilterName: AddOnInvoiceListFilterType) => {
    const filterAlreadySelected = this.state.selectedAddOnFilterNames.includes(selectedFilterName)

    if (filterAlreadySelected) {
      this.setWithoutExistingFilter(selectedFilterName)
    } else {
      this.setSelectedAddOnFilterNames([...this.state.selectedAddOnFilterNames, selectedFilterName])
    }
  }

  applyFilter = async () => {
    try {
      this.setIsFilterApplying(true)

      await this.props.loadInvoices(0)

      // Upon success, add add-on filters' corresponding query params to URL
      this.props.invoiceFilter.apply()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading purchase orders')
    } finally {
      this.setIsFilterApplying(false)
    }
  }

  resetFilter = async () => {
    this.props.invoiceFilter.reset()

    await this.applyFilter()

    this.setSelectedAddOnFilterNames([])
  }

  get allAvailableFilterOptions(): AddOnInvoiceListFilterType[] {
    return [
      AddOnInvoiceListFilterType.PAID_STATUS,
      AddOnInvoiceListFilterType.EMPLOYEE,
      AddOnInvoiceListFilterType.VENDOR,
      AddOnInvoiceListFilterType.PRODUCTS,
      AddOnInvoiceListFilterType.CREATED_TIME,
    ]
  }

  get selectableFilterOptions(): AddOnInvoiceListFilterType[] {
    return this.allAvailableFilterOptions.filter(
      (filterName) => !this.state.selectedAddOnFilterNames.includes(filterName),
    )
  }

  get addOnFilterList(): AddOnFilterList {
    const { invoiceFilter, currentCompanyStore } = this.props
    const employees = currentCompanyStore!.users || []
    const suppliers = currentCompanyStore!.supplierStore!.suppliersWithName || []

    return {
      [AddOnInvoiceListFilterType.PAID_STATUS]: {
        queryKeys: ['paid'],
        renderItem: (
          <span>
            <Select<InvoicePaidStatus | undefined>
              className='gr-filter-item'
              placeholder='Paid status'
              dropdownMatchSelectWidth={false}
              value={invoiceFilter.paid}
              onChange={(value) => (invoiceFilter.paid = value)}
            >
              {this.paidStatusOptions.map((paidStatusOption, index) => (
                <Select.Option key={index} value={paidStatusOption.value}>
                  {paidStatusOption.text}
                </Select.Option>
              ))}
            </Select>
            <Icon
              type='close'
              onClick={() => {
                invoiceFilter.paid = undefined
                this.setWithoutExistingFilter(AddOnInvoiceListFilterType.PAID_STATUS)
              }}
              className='text-muted rounded cursor-pointer gr-filter-close-button'
            />
          </span>
        ),
      },
      [AddOnInvoiceListFilterType.EMPLOYEE]: {
        queryKeys: ['employeeId'],
        renderItem: (
          <span>
            <Select<string | undefined>
              className='gr-filter-item'
              placeholder='Employee'
              dropdownMatchSelectWidth={false}
              value={invoiceFilter.employeeId}
              onChange={(value) => (invoiceFilter.employeeId = value)}
            >
              {employees.map((employee, index) => (
                <Select.Option key={index} value={employee.id}>
                  {employee.name}
                </Select.Option>
              ))}
            </Select>
            <Icon
              type='close'
              onClick={() => {
                invoiceFilter.employeeId = undefined
                this.setWithoutExistingFilter(AddOnInvoiceListFilterType.EMPLOYEE)
              }}
              className='text-muted rounded cursor-pointer gr-filter-close-button'
            />
          </span>
        ),
      },
      [AddOnInvoiceListFilterType.VENDOR]: {
        queryKeys: ['supplierId'],
        renderItem: (
          <span>
            <Select<number | undefined>
              className='gr-filter-item'
              placeholder='Vendor'
              dropdownMatchSelectWidth={false}
              value={invoiceFilter.supplierId}
              onChange={(value) => (invoiceFilter.supplierId = value)}
            >
              {suppliers.map((supplier, index) => (
                <Select.Option key={index} value={supplier.id}>
                  {supplier.name}
                </Select.Option>
              ))}
            </Select>
            <Icon
              type='close'
              onClick={() => {
                invoiceFilter.supplierId = undefined
                this.setWithoutExistingFilter(AddOnInvoiceListFilterType.VENDOR)
              }}
              className='text-muted rounded cursor-pointer gr-filter-close-button'
            />
          </span>
        ),
      },
      [AddOnInvoiceListFilterType.PRODUCTS]: {
        queryKeys: ['selectedProducts'],
        renderItem: (
          <span>
            <AsyncProductInput
              showSearch
              className='gr-filter-item'
              mode='multiple'
              placeholder='Products'
              filterOption={false}
              dropdownMatchSelectWidth={false}
              selectedProducts={invoiceFilter.selectedProducts.map((product) => product.id)}
              onChange={(products: IBaseProduct[]) => {
                invoiceFilter.selectedProducts = products.map((product) => ({
                  id: product.id,
                  name: product.name,
                }))
              }}
            />
            <Icon
              type='close'
              onClick={() => {
                invoiceFilter.selectedProducts = []
                this.setWithoutExistingFilter(AddOnInvoiceListFilterType.PRODUCTS)
              }}
              className='text-muted rounded cursor-pointer gr-filter-close-button'
            />
          </span>
        ),
      },
      [AddOnInvoiceListFilterType.CREATED_TIME]: {
        queryKeys: ['startDate', 'endDate'],
        renderItem: (
          <DateFilter
            startDate={invoiceFilter.startDate}
            endDate={invoiceFilter.endDate}
            flexDirection='row'
            onStartDateChange={(date) => invoiceFilter.setStartDate(date)}
            onEndDateChange={(date) => invoiceFilter.setEndDate(date)}
            onClose={() => {
              invoiceFilter.setStartDate(null)
              invoiceFilter.setEndDate(null)
              this.setWithoutExistingFilter(AddOnInvoiceListFilterType.CREATED_TIME)
            }}
            maxRangeMonths={12}
          />
        ),
      },
    }
  }

  get paidStatusOptions(): Array<{ text: string; value: InvoicePaidStatus }> {
    return [
      {
        text: 'Paid',
        value: InvoicePaidStatus.PAID,
      },
      {
        text: 'Unpaid',
        value: InvoicePaidStatus.UNPAID,
      },
      {
        text: 'Returned',
        value: InvoicePaidStatus.RETURNED,
      },
    ]
  }

  get invoiceStatusOptions(): Array<{ text: string; value: InvoiceStatus }> {
    return [
      {
        text: 'Pending',
        value: InvoiceStatus.PENDING,
      },
      {
        text: 'In progress',
        value: InvoiceStatus.IN_PROGRESS,
      },
      {
        text: 'Completed',
        value: InvoiceStatus.COMPLETED,
      },
      {
        text: 'Cancelled',
        value: InvoiceStatus.CANCELLED,
      },
    ]
  }

  render() {
    const { invoiceFilter, currentCompanyStore } = this.props
    const { selectedAddOnFilterNames, isFilterApplying } = this.state

    const locations = currentCompanyStore!.locations || []

    return (
      <React.Fragment>
        <Input
          className='gr-filter-item gr-filter-gutter'
          placeholder='ID'
          value={invoiceFilter.customId}
          onChange={(e) => (invoiceFilter.customId = e.target.value)}
          onPressEnter={this.applyFilter}
        />

        <Input
          className='gr-filter-item gr-filter-gutter'
          placeholder='Title'
          value={invoiceFilter.title}
          onChange={(e) => (invoiceFilter.title = e.target.value)}
          onPressEnter={this.applyFilter}
        />

        <Select<InvoiceStatus | undefined>
          className='gr-filter-item gr-filter-gutter'
          placeholder='Status'
          allowClear
          dropdownMatchSelectWidth={false}
          value={invoiceFilter.status}
          onChange={(value) => (invoiceFilter.status = value)}
        >
          {this.invoiceStatusOptions.map((statusOption, index) => (
            <Select.Option key={index} value={statusOption.value}>
              {statusOption.text}
            </Select.Option>
          ))}
        </Select>

        <Select<number | undefined>
          className='gr-filter-item gr-filter-gutter'
          placeholder='Location'
          allowClear
          dropdownMatchSelectWidth={false}
          value={invoiceFilter.locationId}
          onChange={(value) => (invoiceFilter.locationId = value)}
        >
          {locations.map((location, index) => (
            <Select.Option key={index} value={location.id}>
              {location.name}
            </Select.Option>
          ))}
        </Select>

        {selectedAddOnFilterNames.map((selectedAddOnFilterName, index) => (
          <div key={index} className='d-flex flex-row gr-filter-gutter'>
            {this.addOnFilterList[selectedAddOnFilterName].renderItem}
          </div>
        ))}

        {this.selectableFilterOptions.length > 0 && (
          <Dropdown
            overlay={
              <Menu
                onClick={(param) =>
                  this.toggleAppliedFilters(param.key as AddOnInvoiceListFilterType)
                }
              >
                {this.selectableFilterOptions.map((option) => (
                  <Menu.Item key={option}>{option}</Menu.Item>
                ))}
              </Menu>
            }
            trigger={['click']}
          >
            <Button type='link' icon='plus' className='pl-0 pr-1 gr-filter-gutter'>
              Add filter
            </Button>
          </Dropdown>
        )}

        <Row className='gr-filter-gutter'>
          <BlazeButton
            buttonProps={{
              type: 'primary',
              loading: isFilterApplying,
              onClick: this.applyFilter,
            }}
            titleProps={{ title: 'Apply' }}
          />

          <BlazeButton
            buttonProps={{
              className: 'text-secondary',
              type: 'link',
              onClick: this.resetFilter,
            }}
            titleProps={{ title: 'Reset' }}
          />
        </Row>
      </React.Fragment>
    )
  }
}
